import notifyWhenBackInStock from "@faire/web-api/api/product/itemToken/notify/retailerToken/back-in-stock/post";
import deleteBackInStockNotification from "@faire/web-api/api/product/productOptionToken/notify/retailerToken/back-in-stock/delete";
import getEventSubscriptions from "@faire/web-api/api/retailer/retailerToken/event-subscriptions/get";
import { EventSubscriptionType } from "@faire/web-api/indigofair/data/EventSubscriptionType";
import { IEventSubscription } from "@faire/web-api/indigofair/data/IEventSubscription";
import { IEventSubscriptionsCompletionStatusEnum } from "@faire/web-api/indigofair/data/IEventSubscriptionsCompletionStatusEnum";
import { UserRole } from "@faire/web-api/indigofair/data/UserRole";
import { logError } from "@faire/web/common/logging";
import { makeObservable } from "@faire/web/common/makeObservable";
import { isWindowUndefined } from "@faire/web/common/server-side-rendering/isWindowUndefined";
import { singletonGetter } from "@faire/web/common/singletons/getter";
import { createStoreHook } from "@faire/web/ui/hooks/useStore";
import { action, computed, observable } from "mobx";

import { RetailerStore } from "./RetailerStore";
import { UserStore } from "./UserStore";

/**
 * @deprecated prefer to use `useEventSubscriptionStore` or `withStores` instead
 */
export class EventSubscriptionStore {
  /**
   * @trackfunction
   */
  static get = singletonGetter(EventSubscriptionStore);

  @observable
  private eventSubscriptions: IEventSubscription[] = [];

  private userStore = UserStore.get();
  private retailerStore = RetailerStore.get();

  constructor() {
    makeObservable(this);

    if (isWindowUndefined()) {
      return;
    }

    this.fetchEventSubscriptions();
  }

  private isValidRetailer = () => {
    // Only check for the existence of retailer token is not sufficient because
    // a brand user can also have a retailer token that looks like r_{brandToken}
    return (
      this.retailerStore.retailer &&
      this.retailerStore.retailer.token &&
      this.userStore.user &&
      this.userStore.user.roles &&
      this.userStore.user.roles.indexOf(UserRole.RETAILER) !== -1
    );
  };

  @action
  private fetchEventSubscriptions = async () => {
    if (this.isValidRetailer()) {
      // Retrieve subscriptions this retailer previously made for back ordered
      // products and product options.
      const retailerToken = this.retailerStore.retailer!.token!;
      const params = {
        type: EventSubscriptionType.BACK_IN_STOCK,
        completed:
          IEventSubscriptionsCompletionStatusEnum
            .EventSubscriptionsCompletionStatus.ONLY_INCOMPLETED,
      };
      const response = await getEventSubscriptions(retailerToken, params);
      this.eventSubscriptions = response.event_subscriptions || [];
    }
  };

  @action
  subscribeBackInStock = (identifier: string) => {
    return notifyWhenBackInStock(
      identifier,
      this.retailerStore.retailer!.token!
    )
      .then(() => this.fetchEventSubscriptions())
      .catch((e) => {
        logError(e);
      });
  };

  @action
  unsubscribeBackInStock = (identifier: string) => {
    return deleteBackInStockNotification(
      identifier,
      this.retailerStore.retailer!.token!
    );
  };

  @computed
  get BackInStockIdentifiers() {
    // Return a list of identifiers (which can be product token or product
    // option token) that's back ordered and been subscribed.
    return this.eventSubscriptions.reduce(
      (accumulator: { [identifer: string]: boolean }, event) => {
        if (event.event_identifier) {
          accumulator[event.event_identifier] = true;
        }
        return accumulator;
      },
      {}
    );
  }
}
export const useEventSubscriptionStore = createStoreHook(
  EventSubscriptionStore
);
